/* eslint-disable */
import { SelectedSubProductDTO } from "@models/managed-accounts/entities/step/fee";
import { FeePortfolioDetailDTO } from "@models/platform-analysis/entities/portfolioDetails";
import { SelectedProductDTO } from "@models/platform-analysis/entities/steps/feature";
import {
  FamilyMemberAccountDTO,
  FeeDetailDTO,
  FeeDTO,
  FeeSubProductDTO,
} from "@models/platform-analysis/entities/steps/fee";
import {
  PortfolioCompositionSectionDTO,
  PortfolioCompositionSetupDTO,
} from "@models/platform-analysis/entities/steps/summary";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeMissingInvestmentsOption } from "@models/platform-analysis/enums/fee/missingInvestments";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";
import {
  formatCurrencyWithFixedDecimals,
  formatNumberFixed,
  getHSLLightness,
  hslToHex,
  thousandSeparator,
} from "@utils";
import { chain, groupBy, isNil, replace } from "lodash";
import {
  DEFAULT_COLOR_TICK,
  DEFAULT_COLORS,
  MAX_LIGHTNESS,
  MIN_LIGHTNESS,
} from "./_id/steps/fee/analysis/components/line-chart/config";
import { FeeAnalysisSpecialProductNames } from "./_id/steps/fee/calculator/components/portfolio-setup/constants";

export const getFeeMinMax = (
  dataKey: keyof Pick<
    FeeDTO,
    "totalCostForDisplay" | "totalCostForDisplayPercentage"
  >,
  subProducts?: FeeSubProductDTO[],
) => {
  let min = Infinity;
  let max = -Infinity;
  if (!subProducts) {
    return { min, max };
  }
  subProducts.forEach((subProduct) => {
    subProduct.fees.forEach((fee) => {
      min = Math.min(min, fee[dataKey]);
      max = Math.max(max, fee[dataKey]);
    });
  });
  return { min, max };
};

export const getFeeMinMaxExcludePerformanceFees = ({
  subProducts,
  isExcludePerformanceFees = false,
  isDollarMode = true,
}: {
  subProducts?: FeeSubProductDTO[];
  isExcludePerformanceFees?: boolean;
  isDollarMode?: boolean;
}) => {
  let min = Infinity;
  let max = -Infinity;

  if (!subProducts) {
    return { min, max };
  }

  const dollarKey: keyof FeeDTO = isExcludePerformanceFees
    ? "totalCostExcPerfForDisplay"
    : "totalCostForDisplay";
  const fallBackDollarKey: keyof FeeDTO = "totalCostForDisplay";

  const percentageKey: keyof FeeDTO = isExcludePerformanceFees
    ? "totalCostExcPerfForDisplayPercentage"
    : "totalCostForDisplayPercentage";
  const fallBackPercentageKey: keyof FeeDTO = "totalCostForDisplayPercentage";

  const dataKey = isDollarMode ? dollarKey : percentageKey;
  const fallBackDataKey = isDollarMode
    ? fallBackDollarKey
    : fallBackPercentageKey;

  subProducts.forEach((subProduct) => {
    subProduct.fees.forEach((fee) => {
      min = Math.min(min, fee[dataKey] || fee[fallBackDataKey] || 0);
      max = Math.max(max, fee[dataKey] || fee[fallBackDataKey] || 0);
    });
  });
  return { min, max };
};

export const getFeeDetailMinMax = (
  dataKey: keyof Pick<FeeDetailDTO, "dollar" | "percentage">,
  subProducts?: FeeSubProductDTO[],
) => {
  if (!subProducts) {
    return [];
  }

  return subProducts[0].feeDetails.map((f, index) => {
    let min = Infinity;
    let max = -Infinity;
    subProducts.forEach((subProduct) => {
      const feeDetail = subProduct.feeDetails[index];
      if (feeDetail) {
        min = Math.min(min, feeDetail[dataKey] || 0);
        max = Math.max(max, feeDetail[dataKey] || 0);
      }
    });
    return { min, max };
  });
};

export const getFeeDetailMinMaxExcludePerformanceFees = ({
  subProducts,
  isExcludePerformanceFees = false,
  isDollarMode = true,
}: {
  subProducts?: FeeSubProductDTO[];
  isExcludePerformanceFees?: boolean;
  isDollarMode?: boolean;
}) => {
  if (!subProducts) {
    return [];
  }

  const dollarKey: keyof FeeDetailDTO = isExcludePerformanceFees
    ? "dollarExcPerf"
    : "dollar";
  const fallBackDollarKey: keyof FeeDetailDTO = "dollar";

  const percentageKey: keyof FeeDetailDTO = isExcludePerformanceFees
    ? "percentageExcPerf"
    : "percentage";
  const fallBackPercentageKey: keyof FeeDetailDTO = "percentage";

  const dataKey = isDollarMode ? dollarKey : percentageKey;
  const fallBackDataKey = isDollarMode
    ? fallBackDollarKey
    : fallBackPercentageKey;

  return subProducts[0].feeDetails.map((_, index) => {
    let min = Infinity;
    let max = -Infinity;
    subProducts.forEach((subProduct) => {
      const feeDetail = subProduct.feeDetails[index];
      if (feeDetail) {
        min = Math.min(
          min,
          feeDetail[dataKey] || feeDetail[fallBackDataKey] || 0,
        );
        max = Math.max(
          max,
          feeDetail[dataKey] || feeDetail[fallBackDataKey] || 0,
        );
      }
    });
    return { min, max };
  });
};

export const displayCurrencyNumber = (num?: number): string => {
  const value = num || 0;
  if (!value) return "";
  return `$${thousandSeparator(
    parseFloat(formatNumberFixed(value, value < Math.pow(10, 6) ? 1 : 2)),
  )}`;
};
export const displayIntNumber = (num?: number): string => {
  const value = num || 0;
  return `${thousandSeparator(parseFloat(formatNumberFixed(value, 0)))}`;
};

export const getPortfolioDetails = (data?: FeePortfolioDetailDTO | null) => {
  let transactionsOutside: string[] = [];
  let transactionsWithin: string[] = [];
  let investments: string[] = [];
  let cfsBalances: string[] = [];
  let expandBalances: string[] = [];
  let northBalances: string[] = [];

  if (data?.idpsCashBalance && data.idpsCashBalance > 0) {
    investments.push(
      formatCurrencyWithFixedDecimals({
        message: "Cash in IDPS accounts",
        value: data.idpsCashBalance,
      }),
    );
  }

  if (data?.superCashBalance && data.superCashBalance > 0) {
    investments.push(
      formatCurrencyWithFixedDecimals({
        message: "Cash in Super/Pension accounts",
        value: data.superCashBalance,
      }),
    );
  }

  if (data?.isChooseMF) {
    if (data.mfBalanceI) {
      investments.push(
        formatCurrencyWithFixedDecimals({
          message: "Managed funds",
          value: data.mfBalanceI,
        }),
      );
    }
    if (data.mfTransactions) {
      transactionsOutside.push(
        `Managed funds: ${displayIntNumber(
          data.mfTransactions,
        )} transactions with an average size of ${formatCurrencyWithFixedDecimals(
          { value: data?.mfAvg },
        )} each`,
      );
    }
  }

  if (data?.isChooseMA) {
    if (data.maBalanceI) {
      investments.push(
        formatCurrencyWithFixedDecimals({
          message: "Managed accounts (SMA or MDA models)",
          value: data.maBalanceI,
        }),
      );
    }

    if (data.maMfTransactions) {
      transactionsWithin.push(
        `Managed funds: ${displayIntNumber(
          data.maMfTransactions,
        )} transactions with an average size of ${formatCurrencyWithFixedDecimals(
          {
            value: data?.maMfAvg,
          },
        )} each`,
      );
    }
    if (data?.maAuTransactions) {
      transactionsWithin.push(
        `Australian listed investments: ${displayIntNumber(
          data?.maAuTransactions,
        )} transactions with an average size of ${formatCurrencyWithFixedDecimals(
          {
            value: data?.maAuAvg,
          },
        )} each`,
      );
    }
    if (data?.maIntlTransactions) {
      transactionsWithin.push(
        `International listed investments: ${displayIntNumber(
          data?.maIntlTransactions,
        )} transactions with an average size of ${formatCurrencyWithFixedDecimals(
          {
            value: data?.maIntlAvg,
          },
        )} each`,
      );
    }

    if (data?.maBondTransactions) {
      transactionsWithin.push(
        `Unlisted bonds: ${displayIntNumber(
          data?.maBondTransactions,
        )} transactions with an average size of ${formatCurrencyWithFixedDecimals(
          {
            value: data?.maBondAvg,
          },
        )} each`,
      );
    }
  }

  if (data?.isChooseAU) {
    if (data.auBalanceI) {
      investments.push(
        formatCurrencyWithFixedDecimals({
          message: "Australian listed investments",
          value: data.auBalanceI,
        }),
      );
    }
    if (data?.auTransactions) {
      transactionsOutside.push(
        `Australian listed investments: ${displayIntNumber(
          data?.auTransactions,
        )} transactions with an average size of ${formatCurrencyWithFixedDecimals(
          {
            value: data?.auAvg,
          },
        )} each`,
      );
    }
  }
  if (data?.isChooseIntl) {
    if (data.intlBalanceI) {
      investments.push(
        formatCurrencyWithFixedDecimals({
          message: "International listed investments",
          value: data.intlBalanceI,
        }),
      );
    }
    if (data?.intlTransactions) {
      transactionsOutside.push(
        `International listed investments: ${displayIntNumber(
          data?.intlTransactions,
        )} transactions with an average size of ${formatCurrencyWithFixedDecimals(
          {
            value: data?.intlAvg,
          },
        )} each`,
      );
    }
  }
  if (data?.isChooseTD) {
    if (data.tdBalanceI) {
      investments.push(
        formatCurrencyWithFixedDecimals({
          message: "Term deposits",
          value: data.tdBalanceI,
        }),
      );
    }
  }

  if (data?.isChooseRetIns) {
    let validRetIns = false;
    if (data?.idpsNumRetIns && data?.idpsNumRetIns > 0) {
      investments.push(
        `Retail insurance policies in IDPS: ${displayIntNumber(
          data.idpsNumRetIns,
        )}`,
      );
      validRetIns = true;
    }

    if (data?.superNumRetIns && data?.superNumRetIns > 0) {
      investments.push(
        `Retail insurance policies in Super: ${displayIntNumber(
          data.superNumRetIns,
        )}`,
      );
      validRetIns = true;
    }

    !validRetIns && investments.push("Retail insurance");
  }

  if (data?.isChooseBond) {
    if (data.bondBalanceI) {
      investments.push(
        formatCurrencyWithFixedDecimals({
          message: "Unlisted bonds",
          value: data.bondBalanceI,
        }),
      );
    }
    if (data?.bondTransactions) {
      transactionsOutside.push(
        `Unlisted bonds: ${displayIntNumber(
          data?.bondTransactions,
        )} transactions with an average size of ${formatCurrencyWithFixedDecimals(
          {
            value: data?.bondAvg,
          },
        )} each`,
      );
    }
  }

  // "Balance in CFS Edge menus" row
  if (data?.edgeFull) {
    cfsBalances.push(
      formatCurrencyWithFixedDecimals({
        message: "CFS Edge Full menu",
        value: data.edgeFull,
      }),
    );
  }
  if (data?.edgeA100) {
    cfsBalances.push(
      formatCurrencyWithFixedDecimals({
        message: "CFS Edge Accelerate 100 SMAs",
        value: data.edgeA100,
      }),
    );
  }
  if (data?.edgeA30) {
    cfsBalances.push(
      formatCurrencyWithFixedDecimals({
        message: "CFS Edge Accelerate 30 SMAs",
        value: data.edgeA30,
      }),
    );
  }
  if (data?.edgeEligible) {
    cfsBalances.push(
      formatCurrencyWithFixedDecimals({
        message: "CFS Edge managed funds eligible for no admin fees",
        value: data.edgeEligible,
      }),
    );
  }

  // "Balance in Expand Essential menus" row
  if (data?.essential) {
    expandBalances.push(
      formatCurrencyWithFixedDecimals({
        message: "Expand Essential",
        value: data.essential,
      }),
    );
  }
  if (data?.essentialPlus) {
    expandBalances.push(
      formatCurrencyWithFixedDecimals({
        message: "Expand Essential+",
        value: data.essentialPlus,
      }),
    );
  }

  // "Balance in North menus" row
  if (data?.northChoice) {
    northBalances.push(
      formatCurrencyWithFixedDecimals({
        message: "North Choice",
        value: data.northChoice,
      }),
    );
  }
  if (data?.northSelect) {
    northBalances.push(
      formatCurrencyWithFixedDecimals({
        message: "North Select",
        value: data.northSelect,
      }),
    );
  }
  if (data?.northGrow) {
    northBalances.push(
      formatCurrencyWithFixedDecimals({
        message: "North Grow",
        value: data.northGrow,
      }),
    );
  }

  return {
    transactionsOutside,
    transactionsWithin,
    investments,
    cfsBalances,
    expandBalances,
    northBalances,
  };
};

export const generateSubProductColors = (subProducts?: FeeSubProductDTO[]) => {
  const groups = groupBy(subProducts, (obj) => obj.productId);
  const colors: { [key in string]: string } = {};
  Object.keys(groups).forEach((key, index) => {
    const group = groups[key];
    const colorRoot = DEFAULT_COLORS[index];
    if (group.length === 1) {
      colors[group[0].id] = hslToHex(
        colorRoot.hue,
        colorRoot.saturation,
        colorRoot.lightness,
      );
    } else {
      const tickColor = (MAX_LIGHTNESS - colorRoot.lightness) / group.length;
      const minLightness =
        tickColor >= DEFAULT_COLOR_TICK
          ? colorRoot.lightness
          : Math.max(
              MAX_LIGHTNESS - DEFAULT_COLOR_TICK * group.length,
              MIN_LIGHTNESS,
            );

      let maxLightness = Math.min(
        colorRoot.lightness + DEFAULT_COLOR_TICK * 1.5,
        MAX_LIGHTNESS,
      );

      if (group.length > 5) {
        maxLightness = Math.min(
          colorRoot.lightness + DEFAULT_COLOR_TICK * (group.length - 1),
          MAX_LIGHTNESS,
        );
      }

      group.forEach((item, itemIndex) => {
        const lightness = getHSLLightness(
          1,
          group.length,
          itemIndex + 1,
          minLightness,
          maxLightness,
        );
        colors[item.id] = hslToHex(
          colorRoot.hue,
          colorRoot.saturation,
          lightness,
        );
      });
    }
  });
  return colors;
};

export const shouldHideSubProductName = (
  setupType: AnalysisFeePortfolioSetupType,
  productName?: string,
) => {
  return (
    FeeAnalysisSpecialProductNames.ProductsToHideSetupNames.includes(
      productName ?? "",
    ) && setupType === AnalysisFeePortfolioSetupType.SubProduct
  );
};

export function removeSubProductNameForSpecificProduct<
  T extends FeeSubProductDTO | SelectedSubProductDTO,
>(subProducts: T[]): T[] {
  return subProducts.map((subProduct) => ({
    ...subProduct,
    name: shouldHideSubProductName(subProduct.setupType, subProduct.productName)
      ? ""
      : subProduct.name,
  }));
}

export const getStringFamilyMemberAccounts = (
  data: FamilyMemberAccountDTO[],
): string => {
  return (
    chain(data)
      ?.map(
        (data) =>
          `${replace(
            data.variableName ? data.variableName : "",
            "_",
            " ",
          )}: ${formatCurrencyWithFixedDecimals({ value: data.balance })}`,
      )
      ?.join(", ")
      ?.value() ?? ""
  );
};

export const getPortfolioCompositionSelectedProducts = ({
  setups,
  hiddenColumnIndexMap,
}: {
  setups?: PortfolioCompositionSetupDTO[];
  hiddenColumnIndexMap: boolean[];
}) => {
  return (
    setups
      ?.map((item, itemIndex) => {
        const {
          id,
          isProductMain,
          productName,
          name: setupName,
          setupType,
          logo,
          order,
        } = item || {};
        const shouldHideName = shouldHideSubProductName(
          setupType as AnalysisFeePortfolioSetupType,
          productName,
        );

        const product: SelectedProductDTO = {
          id: id,
          isProductMain: Boolean(isProductMain),
          order: order,
          productLogo: {
            key: logo,
            url: logo,
          },
          productName: productName,
          subProductName: !shouldHideName ? setupName : undefined,
          totalScore: 0,
          isHidden: hiddenColumnIndexMap[itemIndex],
        };

        return product;
      })
      ?.filter((i) => !i.isHidden) ?? []
  );
};

export const getPortfolioCompositionSelectedSections = ({
  sections,
  hiddenColumnIndexMap,
  startSliceIndex,
  endSliceIndex,
  shouldHideSubTotal,
  autoMarkSubTotal = true,
}: {
  sections?: PortfolioCompositionSectionDTO[];
  hiddenColumnIndexMap: boolean[];
  startSliceIndex?: number;
  endSliceIndex?: number;
  shouldHideSubTotal?: boolean;
  autoMarkSubTotal?: boolean;
}) => {
  return (
    sections?.map((section) => {
      const newSection = { ...section };
      newSection.rows = newSection.rows?.map((row) => {
        const newRow = { ...row };
        newRow.cells = newRow.cells?.filter(
          (_, index) => !hiddenColumnIndexMap[index],
        );

        if (!isNil(startSliceIndex) && !isNil(endSliceIndex)) {
          newRow.cells = newRow.cells?.slice(startSliceIndex, endSliceIndex);
        }

        newRow.isSubTotal =
          shouldHideSubTotal || !autoMarkSubTotal
            ? newRow.isSubTotal
            : newRow.isSubTotal || newSection.rows?.length === 1;
        newRow.isSingleRow = newSection.rows?.length === 1;

        return newRow;
      });

      if (shouldHideSubTotal && autoMarkSubTotal) {
        newSection.rows = newSection.rows?.filter((row) => !row.isSubTotal);
      }

      return newSection;
    }) ?? []
  );
};

export const getFeeAnalysisHiddenSubProductsIndexMap = ({
  feesSubProductsFiltered,
  hiddenIds,
  allSubProducts,
}: {
  feesSubProductsFiltered: FeeSubProductDTO[];
  hiddenIds: string[];
  allSubProducts: FeeSubProductDTO[];
}): boolean[] => {
  if (!feesSubProductsFiltered || !allSubProducts) return [];

  const visibleSubProductIds = new Set(
    feesSubProductsFiltered.map((item) => item.id),
  );

  return allSubProducts.map(
    (item) => hiddenIds.includes(item.id) || !visibleSubProductIds.has(item.id),
  );
};

export const feeAnalysisFilterFeeSubProducts = ({
  feesSubProducts = [],
  feeMissingInvestmentsStyle,
  feeInvestmentMenuStyle,
}: {
  feesSubProducts?: FeeSubProductDTO[];
  feeMissingInvestmentsStyle: FeeMissingInvestmentsOption;
  feeInvestmentMenuStyle: FeeInvestmentMenuOption;
}): FeeSubProductDTO[] => {
  if (
    feeMissingInvestmentsStyle === FeeMissingInvestmentsOption.AllPlatform &&
    feeInvestmentMenuStyle === FeeInvestmentMenuOption.AllPlatform
  ) {
    return feesSubProducts ?? [];
  }

  let feesSubProductsFiltered: FeeSubProductDTO[] = Object.assign(
    feesSubProducts ?? [],
  );

  if (feeInvestmentMenuStyle !== FeeInvestmentMenuOption.AllPlatform) {
    feesSubProductsFiltered = feesSubProductsFiltered?.filter(
      (subProduct) =>
        subProduct.investmentMenu === feeInvestmentMenuStyle ||
        subProduct.multiMenuAccounts,
    );
  }

  if (feeMissingInvestmentsStyle === FeeMissingInvestmentsOption.AllPlatform) {
    return feesSubProductsFiltered;
  } else {
    return feesSubProductsFiltered?.filter((subProduct) =>
      feeMissingInvestmentsStyle === FeeMissingInvestmentsOption.Missing
        ? subProduct.warning
        : !subProduct.warning,
    );
  }
};

export const isAllExpandEssentialSubProductsHidden = ({
  hiddenSubProductIds,
  selectedSubProducts,
}: {
  hiddenSubProductIds: string[];
  selectedSubProducts: FeeSubProductDTO[];
}): boolean => {
  const expandEssentialSubProducts = selectedSubProducts
    ?.filter(
      (subProduct) =>
        subProduct.productName === FeeAnalysisSpecialProductNames.Expand &&
        !subProduct.isRateCard &&
        subProduct.name?.includes(FeeAnalysisSpecialProductNames.Essential),
    )
    ?.map((subProduct) => subProduct.id);
  return expandEssentialSubProducts?.every((subProductId) =>
    hiddenSubProductIds?.some((id) => id === subProductId),
  );
};
